<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :title="getModalTitle()"
    :hide-save-button="!modifiedData.isFromManager || viewOnly"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div v-if="representative" class="pointer-events-none flex flex-col">
        <label for="representative" class="text-sm font-medium">
          {{ $t('representative.representative') }} -{{ representative.name }}
        </label>
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="remoteCode" class="text-sm font-medium">{{
          $t('representative.documentTypeRemotedCode')
        }}</label>
        <BaseInput
          v-model="modifiedData.documentTypeRemotedCode"
          :disabled="mode === 'assign'"
          color="base"
          show-error-as-tooltip
          :error="
            $v.documentTypeRemotedCode.required.$invalid
              ? $v.documentTypeRemotedCode.required.$message
              : $v.documentTypeRemotedCode.uniqueRemoteCode.$invalid
                ? $v.documentTypeRemotedCode.uniqueRemoteCode.$message
                : ''
          " />
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="documentType" class="text-sm font-medium">{{
          $t('representative.documentType')
        }}</label>
        <BaseSearchDropdown
          :list-items="dropdownDocumentTypes"
          v-model="selectedDocumentType"
          :no-results-text="$t('messages.noResultsText')"
          name="documentType"
          show-error-as-tooltip
          internal-filter
          :disabled="!modifiedData.isFromManager || viewOnly"
          :error="$v.documentTypeId.required.$invalid ? $v.documentTypeId.required.$message : ''"
          @item-selected="handleDocumentTypeSelection"
          @clear-selection="handleDocumentTypeSelection" />
      </div>

      <BaseToggle
        :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }"
        :label="$t('representative.toApprove')"
        name="toApprove"
        light
        v-model="modifiedData.toApprove" />

      <BaseSelectDropdown
        :items="exportMethodTypeItems"
        :selected-item="selectedExportMethodType"
        :disable="!modifiedData.isFromManager"
        :label="$t('representative.exportMethodType')"
        :mode="viewOnly ? 'view' : 'edit'"
        @update:selected="handleExportMethodTypeSelection"
        clearable
        name="exportMethodType"
        show-error-as-tooltip
        :error="
          $v.exportMethodType.required.$invalid ? $v.exportMethodType.required.$message : ''
        " />

      <BaseToggle
        :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }"
        :label="$t('representative.allowUpdate')"
        name="allowUpdate"
        light
        v-model="modifiedData.allowUpdate" />

      <BaseToggle
        :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }"
        :label="$t('representative.allowChange')"
        name="allowChange"
        light
        v-model="modifiedData.allowChange" />

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="lastExportedDate" class="text-sm font-medium">{{
          $t('representative.lastExportedDate')
        }}</label>
        <DateTimePicker
          ref="lastExportedDatePickerRef"
          v-model="selectedLastExportedDate"
          :disabled="viewOnly || !modifiedData.isFromManager"
          :placeholder="$t('representative.lastExportedDate')"
          :show-time-picker="false"
          start-empty
          show-error-as-tooltip
          @update:model-value="onDateChange" />
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="exportFolder" class="text-sm font-medium">{{
          $t('representative.exportFolder')
        }}</label>
        <BaseInput
          v-model="modifiedData.exportFolder"
          color="base"
          :disabled="viewOnly || !modifiedData.isFromManager" />
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="exportUrl" class="text-sm font-medium">{{
          $t('representative.exportUrl')
        }}</label>
        <BaseInput
          v-model="modifiedData.exportUrl"
          color="base"
          :disabled="viewOnly || !modifiedData.isFromManager" />
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="exportFileNameFormat" class="text-sm font-medium">{{
          $t('representative.exportFileNameFormat')
        }}</label>
        <BaseInput
          v-model="modifiedData.exportFileNameFormat"
          color="base"
          :disabled="viewOnly || !modifiedData.isFromManager" />
      </div>

      <BaseToggle
        :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }"
        :label="$t('baseObject.isActive')"
        name="isActive"
        light
        v-model="modifiedData.isActive" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';
import { isoDateToDatepickerFormat, datepickerToServerDateFormat } from '@/utils/date';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseToggle from '../common/BaseToggle.vue';
import DateTimePicker from '../common/DateTimePicker.vue';

import { type SelectDropdownItem, RecordMode } from '@/types/Base';
import type { DCDocumentType } from '@/types/DataCollection';
import { eIOMethodType } from '@/types/DataCollection';
import type { Representative } from '@/types/Traders';
import BaseSearchDropdown from '../common/BaseSearchDropdown.vue';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit' | 'view' | 'assign';
    documentType: DCDocumentType;
    representativeId: string;
    representative?: Representative;
    viewOnly?: boolean;
    existingDocumentTypes?: DCDocumentType[];
  }>(),
  {}
);

const { getDocumentTypesLookup } = useLookupsStore();
const { documentTypes: lookupDocumentTypes, dropdownDocumentTypes } =
  storeToRefs(useLookupsStore());

const modifiedData = ref<DCDocumentType>(deepCopy<DCDocumentType>(props.documentType));

const isModified = ref<boolean>(false);

// Convert server date to datepicker format
const selectedLastExportedDate = ref<string>('');
const lastExportedDatePickerRef = ref();
selectedLastExportedDate.value = modifiedData.value.lastExportedDate
  ? isoDateToDatepickerFormat(modifiedData.value.lastExportedDate)
  : '';

const validationModel = ref({
  documentTypeId: modifiedData.value.documentTypeId || '',
  documentTypeRemotedCode: modifiedData.value.documentTypeRemotedCode || '',
  exportMethodType: modifiedData.value.exportMethodType || 0,
  lastExportedDate: selectedLastExportedDate.value || '',
});

// Custom validator for uniqueness
const isUniqueRemoteCode = (value: string) => {
  if (!value || !props.existingDocumentTypes) return true;

  const existingDuplicate = props.existingDocumentTypes.find(
    (docType) =>
      docType.id !== modifiedData.value.id &&
      docType.documentTypeRemotedCode === value &&
      docType.recordMode !== RecordMode.Deleted
  );

  return !existingDuplicate;
};

const rules = computed(() => ({
  documentTypeId: {
    required: helpers.withMessage($t('messages.errors.required'), required),
  },
  documentTypeRemotedCode: {
    required: helpers.withMessage($t('messages.errors.required'), required),
    uniqueRemoteCode: helpers.withMessage(
      $t('messages.errors.duplicateRemoteCode'),
      isUniqueRemoteCode
    ),
  },
  exportMethodType: {
    required: helpers.withMessage($t('messages.errors.required'), required),
  },
  lastExportedDate: {
    validDate: {
      $validator: () => {
        const validation = lastExportedDatePickerRef.value?.getValidationState();
        return !validation || validation.isValid;
      },
      $message: $t('date.invalidDate'),
    },
  },
}));

const $v = useVuelidate(rules, validationModel);

function onDateChange() {
  modifiedData.value.lastExportedDate = datepickerToServerDateFormat(
    selectedLastExportedDate.value
  );
  validationModel.value.lastExportedDate = selectedLastExportedDate.value;
}

watch(
  () => props.documentType,
  (newValue) => {
    modifiedData.value = deepCopy(newValue);
    selectedLastExportedDate.value = newValue.lastExportedDate
      ? isoDateToDatepickerFormat(newValue.lastExportedDate)
      : '';
  },
  { deep: true, immediate: true }
);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.documentTypeId = newData.documentTypeId || '';
    validationModel.value.documentTypeRemotedCode = newData.documentTypeRemotedCode || '';
    validationModel.value.exportMethodType = newData.exportMethodType || 0;
    validationModel.value.lastExportedDate = selectedLastExportedDate.value || '';

    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.documentType);
  },
  { deep: true }
);

const exportMethodTypeItems = computed(() => [
  { name: 'CSV', value: eIOMethodType.Csv },
  { name: 'Fixed Length', value: eIOMethodType.FixedLength },
  { name: 'JSON', value: eIOMethodType.json },
  { name: 'REST API', value: eIOMethodType.RestApi },
]);

const selectedDocumentType = ref<SelectDropdownItem | null>(null);
const selectedExportMethodType = ref<SelectDropdownItem | null>(null);

function onDismissChanges() {
  modifiedData.value = deepCopy<DCDocumentType>(props.documentType);
  emit('closeModal');
}

function onSave() {
  $v.value.$validate();
  if ($v.value.$invalid) return;
  emit('save', modifiedData.value);
}

function handleDocumentTypeSelection(item: SelectDropdownItem) {
  selectedDocumentType.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.documentTypeId = item ? item.value.toString() : '';
    modifiedData.value.documentType = lookupDocumentTypes.value?.find(
      (t) => t.id === modifiedData.value.documentTypeId
    );
  }
}

function handleExportMethodTypeSelection(item: SelectDropdownItem) {
  selectedExportMethodType.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.exportMethodType = item ? Number(item.value) : 0;
  }
}

onMounted(async () => {
  await getDocumentTypesLookup();

  if (modifiedData.value && modifiedData.value.documentTypeId) {
    const foundType = lookupDocumentTypes.value.find(
      (t) => t.id === modifiedData.value.documentTypeId
    );
    if (foundType) {
      selectedDocumentType.value = {
        name: foundType.name as string,
        value: foundType.id,
      };
    }
  }

  if (modifiedData.value && modifiedData.value.exportMethodType) {
    const foundMethodType = exportMethodTypeItems.value.find(
      (t) => t.value === modifiedData.value.exportMethodType
    );
    if (foundMethodType) {
      selectedExportMethodType.value = foundMethodType;
    }
  }
});

function getModalTitle() {
  const actionKey =
    props.viewOnly || props.mode === 'view' ? 'view' : props.mode === 'add' ? 'create' : 'edit';
  return `${$t(`common.actions.${actionKey}`)} ${$t('representative.entities.documentType')}`;
}
</script>
